import { useCallback, useState } from 'react';
import { useDownloadAssessmentPdfMutation } from '@common/cache/mutations/assessment';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting';

export const useDownloadAssessmentPdf = () => {
  const { mutateAsync, isPending } = useDownloadAssessmentPdfMutation();
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const downloadPdf = useCallback(
    async (patientId: string, assessmentId: string) => {
      const key = `${patientId}_${assessmentId}`;

      try {
        setDownloadingIds(prev => new Set(prev).add(key));

        const response = await mutateAsync({
          patient_id: patientId,
          assessment_id: assessmentId,
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers['content-disposition'];
        const fileNameMatch = contentDisposition?.match(
          /filename[*]?=['"]?([^'";]+)/,
        );
        link.download = fileNameMatch ? fileNameMatch[1] : 'assessment.pdf';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => URL.revokeObjectURL(url), 150);

        toast.success('PDF downloaded successfully');

        setDownloadingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });
      } catch (error) {
        setDownloadingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });

        toast.error(formatApiError(error));
      }
    },
    [mutateAsync],
  );

  return { downloadPdf, isDownloading: isPending || downloadingIds.size > 0 };
};
